import type { TwyfordSentryConfig } from '@/core/types'

/**
 * 校验错误类
 */
export class ValidationError extends Error {
  constructor(message: string, public field?: string, public value?: any) {
    super(message)
    this.name = 'ValidationError'
  }
}

/**
 * 校验结果
 */
export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
}

/**
 * 校验规则
 */
type ValidatorRule<T> = {
  validate: (value: T) => boolean
  message: string
}

/**
 * 通用校验器
 */
export class Validator {
  /**
   * 校验是否为空
   */
  static isNotEmpty(value: any): boolean {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim().length > 0
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value).length > 0
    return true
  }

  /**
   * 校验是否为字符串
   */
  static isString(value: any): boolean {
    return typeof value === 'string'
  }

  /**
   * 校验是否为数字
   */
  static isNumber(value: any): boolean {
    return typeof value === 'number' && !isNaN(value)
  }

  /**
   * 校验是否为布尔值
   */
  static isBoolean(value: any): boolean {
    return typeof value === 'boolean'
  }

  /**
   * 校验是否为对象
   */
  static isObject(value: any): boolean {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  }

  /**
   * 校验是否为数组
   */
  static isArray(value: any): boolean {
    return Array.isArray(value)
  }

  /**
   * 校验是否为函数
   */
  static isFunction(value: any): boolean {
    return typeof value === 'function'
  }

  /**
   * 校验是否为有效的 URL
   */
  static isValidUrl(value: string): boolean {
    try {
      new URL(value)
      return true
    } catch {
      return false
    }
  }

  /**
   * 校验是否为有效的 Email
   */
  static isValidEmail(value: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value)
  }

  /**
   * 校验是否在范围内
   */
  static isInRange(value: number, min: number, max: number): boolean {
    return value >= min && value <= max
  }

  /**
   * 校验字符串长度
   */
  static hasLength(value: string, min: number, max?: number): boolean {
    const length = value.length
    if (max !== undefined) {
      return length >= min && length <= max
    }
    return length >= min
  }

  /**
   * 校验是否匹配正则
   */
  static matches(value: string, pattern: RegExp): boolean {
    return pattern.test(value)
  }

  /**
   * 校验是否为有效的 DSN
   */
  static isValidDsn(dsn: string): boolean {
    // Sentry DSN 格式: https://public@sentry.io/project-id
    const dsnRegex = /^https?:\/\/[a-zA-Z0-9]+@[^/]+\/\d+$/
    return dsnRegex.test(dsn)
  }

  /**
   * 校验环境名称
   */
  static isValidEnvironment(env: string): boolean {
    const validEnvs = ['development', 'staging', 'production', 'test']
    return validEnvs.includes(env)
  }

  /**
   * 校验采样率
   */
  static isValidSampleRate(rate: number): boolean {
    return this.isNumber(rate) && this.isInRange(rate, 0, 1)
  }
}

/**
 * 校验 Sentry 配置
 */
export function validateConfig(
  config: TwyfordSentryConfig
): ValidationError | null {
  const errors: ValidationError[] = []

  // 校验 DSN
  if (!config.dsn) {
    errors.push(new ValidationError('DSN is required', 'dsn', config.dsn))
  } else if (!Validator.isValidDsn(config.dsn)) {
    errors.push(new ValidationError('Invalid DSN format', 'dsn', config.dsn))
  }

  // 校验环境
  if (!config.environment) {
    errors.push(
      new ValidationError(
        'Environment is required',
        'environment',
        config.environment
      )
    )
  } else if (!Validator.isValidEnvironment(config.environment)) {
    errors.push(
      new ValidationError(
        'Invalid environment. Must be one of: development, staging, production',
        'environment',
        config.environment
      )
    )
  }

  // 校验应用名称
  if (!config.appName) {
    errors.push(
      new ValidationError('App name is required', 'appName', config.appName)
    )
  } else if (!Validator.hasLength(config.appName, 1, 100)) {
    errors.push(
      new ValidationError(
        'App name must be between 1 and 100 characters',
        'appName',
        config.appName
      )
    )
  }

  // 校验应用版本
  if (!config.appVersion) {
    errors.push(
      new ValidationError(
        'App version is required',
        'appVersion',
        config.appVersion
      )
    )
  }

  // 校验采样率
  if (config.tracesSampleRate !== undefined) {
    if (!Validator.isValidSampleRate(config.tracesSampleRate)) {
      errors.push(
        new ValidationError(
          'Traces sample rate must be between 0 and 1',
          'tracesSampleRate',
          config.tracesSampleRate
        )
      )
    }
  }

  if (config.replaysSessionSampleRate !== undefined) {
    if (!Validator.isValidSampleRate(config.replaysSessionSampleRate)) {
      errors.push(
        new ValidationError(
          'Replays session sample rate must be between 0 and 1',
          'replaysSessionSampleRate',
          config.replaysSessionSampleRate
        )
      )
    }
  }

  if (config.replaysOnErrorSampleRate !== undefined) {
    if (!Validator.isValidSampleRate(config.replaysOnErrorSampleRate)) {
      errors.push(
        new ValidationError(
          'Replays on error sample rate must be between 0 and 1',
          'replaysOnErrorSampleRate',
          config.replaysOnErrorSampleRate
        )
      )
    }
  }

  // 校验用户邮箱
  if (config.userEmail && !Validator.isValidEmail(config.userEmail)) {
    errors.push(
      new ValidationError('Invalid email format', 'userEmail', config.userEmail)
    )
  }

  // 校验 ignoreErrors
  if (config.ignoreErrors) {
    if (!Validator.isArray(config.ignoreErrors)) {
      errors.push(
        new ValidationError(
          'ignoreErrors must be an array',
          'ignoreErrors',
          config.ignoreErrors
        )
      )
    }
  }

  // 校验 allowUrls
  if (config.allowUrls) {
    if (!Validator.isArray(config.allowUrls)) {
      errors.push(
        new ValidationError(
          'allowUrls must be an array',
          'allowUrls',
          config.allowUrls
        )
      )
    }
  }

  // 校验 customTags
  if (config.customTags) {
    if (!Validator.isObject(config.customTags)) {
      errors.push(
        new ValidationError(
          'customTags must be an object',
          'customTags',
          config.customTags
        )
      )
    } else {
      // 校验标签值都是字符串
      for (const [key, value] of Object.entries(config.customTags)) {
        if (!Validator.isString(value)) {
          errors.push(
            new ValidationError(
              `Tag value for "${key}" must be a string`,
              `customTags.${key}`,
              value
            )
          )
        }
      }
    }
  }

  // 校验 beforeSend
  if (config.beforeSend && !Validator.isFunction(config.beforeSend)) {
    errors.push(
      new ValidationError(
        'beforeSend must be a function',
        'beforeSend',
        config.beforeSend
      )
    )
  }

  // 校验 beforeBreadcrumb
  if (
    config.beforeBreadcrumb &&
    !Validator.isFunction(config.beforeBreadcrumb)
  ) {
    errors.push(
      new ValidationError(
        'beforeBreadcrumb must be a function',
        'beforeBreadcrumb',
        config.beforeBreadcrumb
      )
    )
  }

  if (window.TwyfordSentry && errors.length > 0) {
    window.TwyfordSentry.logs.push(errors)
  }

  return errors.length > 0 ? errors[0] : null
}

/**
 * 批量校验
 */
export function validateAll(config: TwyfordSentryConfig): ValidationResult {
  const errors: ValidationError[] = []

  try {
    const error = validateConfig(config)
    if (error) {
      errors.push(error)
    }
  } catch (e) {
    errors.push(
      new ValidationError('Validation failed: ' + (e as Error).message)
    )
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * 断言校验
 */
export function assertValid(config: TwyfordSentryConfig): void {
  const error = validateConfig(config)
  if (error) {
    throw error
  }
}
